import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Form, Spinner, ProgressBar, ListGroup } from 'react-bootstrap';
import { FiUpload, FiFileText, FiMessageSquare, FiTrash2, FiEye, FiCpu, FiCopy } from 'react-icons/fi';
import { FaSlack } from 'react-icons/fa';
import axios from '../utils/axios';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const DocumentAnalysis = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatting, setIsChatting] = useState(false);
  const [useVision, setUseVision] = useState(false); // Vision AI for PDFs with images/charts
  const [processingStatus, setProcessingStatus] = useState('');
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      uploadFiles(files);
    }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    setProcessingStatus('Uploading...');

    const formData = new FormData();
    files.forEach(file => {
      formData.append('documents', file);
    });
    
    // Add vision flag
    formData.append('useVision', useVision);

    try {
      if (useVision) {
        setProcessingStatus('Uploading & analyzing with AI Vision (this may take a minute)...');
      }
      
      const response = await axios.post('/api/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes for vision analysis
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          if (percentCompleted === 100 && useVision) {
            setProcessingStatus('Processing with AI Vision...');
          }
        }
      });
      
      const visionDocs = response.data.documents?.filter(d => d.analysisMethod === 'vision').length || 0;
      if (visionDocs > 0) {
        toast.success(`${files.length} document(s) uploaded with AI Vision analysis`);
      } else {
        toast.success(`${files.length} document(s) uploaded successfully`);
      }
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading files:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to upload documents');
      } else {
        toast.error('Failed to upload files: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setProcessingStatus('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleDocumentSelection = (docId) => {
    setSelectedDocIds(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    
    if (selectedDocIds.length === 0) {
      toast.error('Please select at least one document to chat with');
      return;
    }

    const userMessage = chatInput;
    setChatInput('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatting(true);

    try {
      const response = await axios.post('/api/documents/chat', {
        message: userMessage,
        documentIds: selectedDocIds
      }, {
        timeout: 120000 // 2 minutes - AI with large docs can take time
      });
      
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.reply }]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to get response');
      setChatHistory(prev => [...prev, { role: 'error', content: 'Failed to get response. Please try again.' }]);
    } finally {
      setIsChatting(false);
    }
  };

  const deleteDocument = async (docId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`/api/documents/${docId}`);
      toast.success('Document deleted');
      setDocuments(prev => prev.filter(d => d._id !== docId));
      setSelectedDocIds(prev => prev.filter(id => id !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const sendToSlack = async (content) => {
    try {
      // Get selected document names for context
      const selectedDocs = documents.filter(d => selectedDocIds.includes(d._id));
      const docNames = selectedDocs.map(d => d.name).join(', ');
      
      const message = `📄 *Document Analysis*\n_Documents: ${docNames}_\n\n${content}`;
      
      await axios.post('/api/integrations/slack/send', {
        message,
        channel: 'general' // Or could let user pick
      });
      toast.success('Sent to Slack!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Slack not connected. Go to Settings to connect.');
      } else {
        toast.error('Failed to send to Slack');
      }
    }
  };

  return (
    <Container fluid className="document-analysis-container p-0">
      <div className="document-analysis-layout">
        
        {/* Sidebar - Documents List */}
        <div className="document-sidebar border-end d-flex flex-column">
          <div className="p-3 border-bottom document-sidebar-header">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Documents</h5>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <FiUpload className="me-2" /> Upload
              </Button>
            </div>
            
            {/* Vision Toggle */}
            <div className="d-flex align-items-center justify-content-between p-2 rounded" style={{ backgroundColor: useVision ? 'rgba(var(--bs-primary-rgb), 0.1)' : 'transparent' }}>
              <div className="d-flex align-items-center">
                <FiEye className={`me-2 ${useVision ? 'text-primary' : 'text-muted'}`} />
                <small className={useVision ? 'text-primary fw-bold' : 'text-muted'}>
                  AI Vision (OCR, charts, images)
                </small>
              </div>
              <Form.Check 
                type="switch"
                checked={useVision}
                onChange={(e) => setUseVision(e.target.checked)}
                disabled={uploading}
              />
            </div>
            
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              multiple 
              accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg"
              onChange={handleFileSelect}
            />
          </div>

          {uploading && (
            <div className="p-3 border-bottom">
              <div className="d-flex justify-content-between mb-1">
                <small>{processingStatus || 'Uploading...'}</small>
                <small>{uploadProgress}%</small>
              </div>
              <ProgressBar now={uploadProgress} animated={useVision && uploadProgress === 100} />
              {useVision && uploadProgress === 100 && (
                <small className="text-muted d-block mt-1">
                  <FiCpu className="me-1" /> Analyzing document with AI Vision...
                </small>
              )}
            </div>
          )}

          <div className="flex-grow-1 overflow-auto p-2">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <FiFileText size={48} className="mb-3 opacity-50" />
                <p>No documents uploaded yet.</p>
                <p className="small">Upload PDF, DOCX, or Text files to start analyzing.</p>
              </div>
            ) : (
              <ListGroup variant="flush">
                {documents.map(doc => (
                  <ListGroup.Item 
                    key={doc._id} 
                    action 
                    active={selectedDocIds.includes(doc._id)}
                    onClick={() => toggleDocumentSelection(doc._id)}
                    className="d-flex align-items-center border rounded mb-2"
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={selectedDocIds.includes(doc._id)}
                      onChange={() => {}} // Handled by list item click
                      className="me-3"
                      style={{ pointerEvents: 'none' }}
                    />
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-bold text-truncate" title={doc.name}>{doc.name}</div>
                      <div className="small text-muted d-flex justify-content-between">
                        <span>{doc.fileType?.toUpperCase() || 'FILE'} • {doc.formattedSize || formatFileSize(doc.size)}</span>
                      </div>
                    </div>
                    <Button 
                      variant="link" 
                      className="text-danger p-1 ms-2 opacity-50 hover-opacity-100"
                      onClick={(e) => deleteDocument(doc._id, e)}
                    >
                      <FiTrash2 />
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
          
          <div className="p-3 border-top document-sidebar-footer">
            <small className="text-muted">
              Selected: {selectedDocIds.length} document{selectedDocIds.length !== 1 ? 's' : ''}
            </small>
          </div>
        </div>

        {/* Main Content - Chat Interface */}
        <div className="document-main-content d-flex flex-column">
          <div className="p-3 border-bottom shadow-sm d-flex justify-content-between align-items-center document-chat-header">
            <div>
              <h4 className="mb-0">Document Analysis</h4>
              <p className="text-muted mb-0 small">
                {selectedDocIds.length > 0 
                  ? `Chatting with ${selectedDocIds.length} selected document(s)` 
                  : 'Select documents from the sidebar to start chatting'}
              </p>
            </div>
          </div>

          <div className="flex-grow-1 overflow-auto p-4 document-chat-area">
            {chatHistory.length === 0 ? (
              <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted opacity-50">
                <FiMessageSquare size={64} className="mb-3" />
                <h3>Ask questions to your documents</h3>
                <p>Select documents and type your question below</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3" style={{ maxWidth: '800px', margin: '0 auto' }}>
                {chatHistory.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                  >
                    <div 
                      className={`p-3 rounded-3 shadow-sm ${
                        msg.role === 'user' 
                          ? 'bg-primary text-white' 
                          : 'document-chat-bubble-ai'
                      }`}
                      style={{ maxWidth: '80%' }}
                    >
                      {msg.role === 'assistant' ? (
                        <div className="markdown-content">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                      )}
                      
                      {/* Action buttons for AI responses */}
                      {msg.role === 'assistant' && (
                        <div className="mt-2 pt-2 border-top d-flex gap-2">
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => copyToClipboard(msg.content)}
                            title="Copy to clipboard"
                          >
                            <FiCopy className="me-1" /> Copy
                          </Button>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => sendToSlack(msg.content)}
                            title="Send to Slack"
                          >
                            <FaSlack className="me-1" /> Slack
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isChatting && (
                  <div className="d-flex justify-content-start">
                    <div className="document-chat-bubble-ai p-3 rounded-3 shadow-sm">
                      <Spinner animation="border" size="sm" className="me-2" /> Thinking...
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          <div className="p-4 border-top document-chat-input-area">
            <Form onSubmit={handleSendMessage} style={{ maxWidth: '800px', margin: '0 auto' }}>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder={selectedDocIds.length > 0 ? "Ask a question about the selected documents..." : "Select documents to start chatting"}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled={isChatting || selectedDocIds.length === 0}
                  className="p-3 shadow-sm"
                />
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={isChatting || !chatInput.trim() || selectedDocIds.length === 0}
                  className="px-4"
                >
                  Send
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default DocumentAnalysis;
